import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { TextField, Button } from '../../components/primitives';
import { ResourceForm } from '../../components/domain/resource';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Step 2: Create Resource
 * Searchable dropdown for selecting an existing resource for the selected facility
 */
const CreateResource = ({
  selectedFacility,
  onResourceSelect,
  selectedResource: propSelectedResource,
  onFooterChange,
}) => {
  const [resources, setResources] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedResource, setSelectedResource] = useState(propSelectedResource || null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openAddResource, setOpenAddResource] = useState(false);
  const containerRef = useRef(null);

  // Format resource display name
  const formatResourceDisplay = (resource) => {
    return resource.name || 'Unnamed Resource';
  };

  // Fetch resources when facility is selected
  useEffect(() => {
    if (selectedFacility?.id) {
      fetchResources(selectedFacility.id);
    } else {
      setResources([]);
      setSelectedResource(null);
      setSearchText('');
    }
  }, [selectedFacility]);

  // Sync with prop when it changes from parent
  useEffect(() => {
    if (propSelectedResource) {
      setSelectedResource(propSelectedResource);
      setSearchText(formatResourceDisplay(propSelectedResource));
    } else if (propSelectedResource === null && selectedResource !== null) {
      // Clear selection if prop is explicitly set to null
      setSelectedResource(null);
      setSearchText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propSelectedResource]);

  const fetchResources = async (facilityId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/resources/?facility=${facilityId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResources(data);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Failed to load resources. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter resources based on search text
  const filteredResources = useMemo(() => {
    if (!searchText.trim()) {
      return resources;
    }

    const searchLower = searchText.toLowerCase();
    return resources.filter((resource) => {
      const nameMatch = resource.name?.toLowerCase().includes(searchLower);
      return nameMatch;
    });
  }, [resources, searchText]);

  // Get display value for input field
  const inputValue = selectedResource ? formatResourceDisplay(selectedResource) : searchText;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setSelectedResource(null); // Clear selection when user types
  };

  const handleInputFocus = () => {
    // If a resource is selected, clear it and allow new search
    if (selectedResource) {
      setSelectedResource(null);
      setSearchText('');
    }
    setShowDropdown(true);
  };

  const handleResourceSelect = (resource) => {
    setSelectedResource(resource);
    setSearchText(formatResourceDisplay(resource));
    setShowDropdown(false); // Hide dropdown after selection
    // Pass selection to parent component
    if (onResourceSelect) {
      onResourceSelect(resource);
    }
  };

  const handleResourceSaved = useCallback((newResource) => {
    setResources((prev) => [newResource, ...prev]);
    setSelectedResource(newResource);
    setSearchText(formatResourceDisplay(newResource));
    setShowDropdown(false);
    if (onResourceSelect) {
      onResourceSelect(newResource);
    }
    setOpenAddResource(false);
  }, [onResourceSelect]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!onFooterChange) return undefined;

    if (!selectedFacility || loading || error) {
      onFooterChange(null);
      return () => onFooterChange(null);
    }

    onFooterChange(
      <>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          Can't find what you're looking for?
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => setOpenAddResource(true)}>
            Add Resource
          </Button>
        </Box>

        <Dialog
          open={openAddResource}
          onClose={() => setOpenAddResource(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ pt: 3 }}>
            <ResourceForm
              facilityId={selectedFacility?.id}
              onSaved={handleResourceSaved}
              showActions={true}
              submitLabel="Submit"
            />
          </DialogContent>
        </Dialog>
      </>
    );

    return () => onFooterChange(null);
  }, [
    selectedFacility,
    loading,
    error,
    openAddResource,
    onFooterChange,
    handleResourceSaved,
  ]);

  if (!selectedFacility) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Please select a facility first.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }} ref={containerRef}>
      {/* Title */}
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          mb: 2,
          color: 'text.primary',
        }}
      >
        Choose from existing resources:
      </Typography>

      {/* Search Input */}
      <TextField
        fullWidth
        placeholder="Start typing resource name..."
        value={inputValue}
        onChange={handleSearchChange}
        onFocus={handleInputFocus}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
          },
        }}
      />

      {/* Dropdown List */}
      {showDropdown && filteredResources.length > 0 ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 400,
            overflowY: 'auto',
            backgroundColor: 'background.paper',
          }}
        >
          <List disablePadding>
            {filteredResources.map((resource, index) => (
              <React.Fragment key={resource.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleResourceSelect(resource)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 400,
                      }}
                    >
                      {formatResourceDisplay(resource)}
                    </Typography>
                  </ListItemButton>
                </ListItem>
                {index < filteredResources.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      ) : showDropdown && searchText.trim() ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No resources found matching your search.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default CreateResource;
